/**
 * <workshop-feed> - renders "build" cards from a JSON feed on revzone.com.au.
 *
 * Liquid can't fetch an external URL at render time, so this fetches the feed client-side and builds
 * the cards. The feed shape (see assets/workshop-feed-sample.json):
 *   { "builds": [ { title, url, image, vehicle?, description?, date? }, ... ] }
 * title/url/image are required per card; the rest are optional.
 *
 * The whole section is hidden until at least one card renders, so a slow/unreachable/empty feed
 * never leaves a lonely heading on the page. In the theme editor (design mode) the section stays
 * visible with its server-rendered placeholders so the merchant can configure it.
 *
 * Card text is set via textContent and urls are validated as http(s), so a malformed feed can't
 * inject markup.
 */

class WorkshopFeed extends HTMLElement {
  async connectedCallback() {
    this.feedUrl = this.getAttribute('data-feed-url') || '';
    this.count = parseInt(this.getAttribute('data-count') || '3', 10);
    this.newTab = this.getAttribute('data-new-tab') === 'true';
    this.grid = this.querySelector('[data-workshop-cards]');
    this.section = this.closest('.workshop-feed');
    this.designMode = this.section?.hasAttribute('data-design-mode');

    if (!this.feedUrl || !this.grid) return;

    try {
      const res = await fetch(this.feedUrl, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const builds = Array.isArray(data?.builds) ? data.builds : [];
      const cards = builds.slice(0, this.count).map((b) => this.#card(b)).filter(Boolean);

      if (cards.length) {
        this.grid.replaceChildren(...cards);
        this.#reveal();
      } else if (!this.designMode) {
        this.#hide();
      }
    } catch (error) {
      console.warn('[workshop-feed] feed unavailable:', error.message);
      if (!this.designMode) this.#hide();
    }
  }

  /** @param {Record<string, unknown>} b */
  #card(b) {
    if (!b || typeof b.title !== 'string' || !this.#isHttp(b.url)) return null;

    const card = document.createElement('a');
    card.className = 'workshop-feed__card';
    card.href = b.url;
    if (this.newTab) {
      card.target = '_blank';
      card.rel = 'noopener';
    }

    if (this.#isHttp(b.image)) {
      const media = document.createElement('div');
      media.className = 'workshop-feed__media';
      const img = document.createElement('img');
      img.className = 'workshop-feed__img';
      img.src = /** @type {string} */ (b.image);
      img.alt = b.title;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.addEventListener('error', () => media.classList.add('workshop-feed__media--empty'));
      media.appendChild(img);
      card.appendChild(media);
    }

    const body = document.createElement('div');
    body.className = 'workshop-feed__body';

    const title = document.createElement('h3');
    title.className = 'workshop-feed__card-title';
    title.textContent = b.title;
    body.appendChild(title);

    if (typeof b.description === 'string' && b.description.trim()) {
      const desc = document.createElement('p');
      desc.className = 'workshop-feed__desc';
      desc.textContent = b.description;
      body.appendChild(desc);
    }

    card.appendChild(body);
    return card;
  }

  #isHttp(value) {
    return typeof value === 'string' && /^https?:\/\//i.test(value);
  }

  #reveal() {
    if (this.section) this.section.hidden = false;
  }

  #hide() {
    if (this.section) this.section.hidden = true;
  }
}

if (!customElements.get('workshop-feed')) {
  customElements.define('workshop-feed', WorkshopFeed);
}
